import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useUser } from '~/api/user';
import { Field, FieldGroup, NumberInput, Select } from '~/components/Form';
import { useIntl } from '~/hooks/useIntl';
import { srOnlyStyle } from '~/styles/utilities.css';
import { clsx } from '~/styles/utils';
import { insertIf } from '~/utils/arrays';
import { isWriter } from '~/utils/user';

import { useFormWithLinesContext } from '../../FormLines.context';
import { useSectionContext } from '../../Section/Section.context';
import { useLineContext } from '../../SectionLines/SectionLines.context';

export const FormQuantity = () => {
  const { data: user } = useUser();

  const { control, watch } = useFormWithLinesContext();
  const { required } = useSectionContext();
  const { isMobile, lineFieldName } = useLineContext();

  const watchQuantity = watch(`${lineFieldName}.quantity`);
  const watchUnit = watch(`${lineFieldName}.unit`);

  const unitOptions = [
    null,
    'hours',
    'halfDays',
    'days',
    'kilometers',
    ...insertIf(isWriter(user) || watchUnit === 'words', 'words'),
    ...insertIf(isWriter(user) || watchUnit === 'characters', 'characters'),
  ] as const;

  const { formatDecimal } = useIntl();
  const { t } = useTranslation(['lines', 'validation']);

  const unitItems = unitOptions.map((unit) => ({
    label: unit === null ? t('lines:units.none') : t(`lines:units.${unit}`, { count: watchQuantity }),
    value: unit,
  }));

  return (
    <FieldGroup.Root>
      <FieldGroup.Legend className={clsx(!isMobile && srOnlyStyle)}>{t('lines:quantity.header')}</FieldGroup.Legend>

      <FieldGroup.Items>
        <Controller
          control={control}
          name={`${lineFieldName}.quantity`}
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} grow invalid={invalid} name={name} touched={isTouched}>
              <NumberInput onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />

              {isMobile && <Field.Error match={!!error}>{error?.message}</Field.Error>}
            </Field.Root>
          )}
          rules={{
            required: { value: required, message: t('validation:required') },
            min: {
              value: required ? 0.01 : 0,
              message: t('validation:min', { attribute: t('lines:quantity.header'), min: formatDecimal(0.01) }),
            },
            max: {
              value: 100_000,
              message: t('validation:max', { attribute: t('lines:quantity.header'), max: formatDecimal(100_000) }),
            },
          }}
        />

        <Controller
          control={control}
          name={`${lineFieldName}.unit`}
          render={({ field: { name, onChange, ref, value } }) => (
            <Field.Root name={name}>
              <Select.Root items={unitItems} onValueChange={onChange} value={value}>
                <Select.Button placeholder={t('lines:units.placeholder')} ref={ref} />
                <Select.List align="end" minWidth="150px">
                  {unitItems.map((item) => (
                    <Select.Item key={item.value ?? 'none'} value={item.value}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.List>
              </Select.Root>
            </Field.Root>
          )}
        />
      </FieldGroup.Items>
    </FieldGroup.Root>
  );
};
